// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IOLSettings from "./IOLSettings/IOLSettings";
import ProgressiveSettings from "./ProgressiveSettings/ProgressiveSettings";
import DryEyeSettings from "./DryEyeSettings/DryEyeSettings";

// Styles
import { useTheme } from "@mui/material/styles";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type LensOptionSettingsType = {
  isLoadingSettings: boolean;
  isUpdatingSettings: boolean;
  currentSettings: UpdateClinicSettingsType;
  handleUpdateSettings: (settings: UpdateClinicSettingsType) => void;
};

const LensOptionSettings = ({
  isLoadingSettings,
  isUpdatingSettings,
}: LensOptionSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const canDisplayIOLSim = useMemo(
    () => clinicInfo?.licensing?.canUseIOLSimulations,
    [clinicInfo?.licensing?.canUseIOLSimulations]
  );

  const canDisplayProgressiveSim = useMemo(
    () => clinicInfo?.licensing?.canUseProgressiveSimulations,
    [clinicInfo?.licensing?.canUseProgressiveSimulations]
  );

  const canDisplayDryEyeSim = useMemo(
    () => clinicInfo?.licensing?.canUseDryEyeSimulations,
    [clinicInfo?.licensing?.canUseDryEyeSimulations]
  );

  return (
    <>
      <Typography
        sx={{ textTransform: "uppercase" }}
        fontSize="16px"
        color={mutedText}
      >
        {t("Simulation Settings")}
      </Typography>

      <Box display="flex" flexWrap="wrap" gap="16px">
        {canDisplayIOLSim && (
          <Box sx={{ flex: "1 1 400px" }}>
            <IOLSettings
              isLoadingSettings={isLoadingSettings}
              isUpdatingSettings={isUpdatingSettings}
            />
          </Box>
        )}

        {canDisplayProgressiveSim && (
          <Box sx={{ flex: "1 1 400px" }}>
            <ProgressiveSettings
              isLoadingSettings={isLoadingSettings}
              isUpdatingSettings={isUpdatingSettings}
            />
          </Box>
        )}

        {canDisplayDryEyeSim && (
          <Box sx={{ flex: "1 1 400px" }}>
            <DryEyeSettings
              isLoadingSettings={isLoadingSettings}
              isUpdatingSettings={isUpdatingSettings}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default LensOptionSettings;
